import {copyText} from '@github-ui/copy-to-clipboard'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CopyIcon, TerminalIcon, VscodeIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Tooltip, Truncate} from '@primer/react'
import {useCallback, useMemo} from 'react'

import {ClaudeCodeIcon} from './ClaudeCodeIcon'
import {CodexIcon} from './CodexIcon'
import styles from './OpenInLocalAgentButton.module.css'

type OpenInLocalAgentButtonProps = {
  url: string
}

type OpenInTool = {
  icon: React.ComponentType
  label: string
  url: string
}

function buildPrompt(issueUrl: string): string {
  return `Work on the following GitHub issue: ${issueUrl}.`
}

function buildCodexUrl(prompt: string): string {
  const encoded = encodeURIComponent(prompt)
  return `codex://new?prompt=${encoded}`
}

function buildClaudeCodeUrl(prompt: string): string {
  const encoded = encodeURIComponent(prompt)
  return `https://claude.ai/code?prompt=${encoded}`
}

function buildVSCodeAgentUrl(prompt: string): string {
  const doubleEncoded = encodeURIComponent(encodeURIComponent(prompt))
  return `vscode://github.copilot-chat?mode=agent&prompt=${doubleEncoded}`
}

export function OpenInLocalAgentButton({url}: OpenInLocalAgentButtonProps) {
  const enabled = isFeatureEnabled('issues_open_in_tool_links')
  if (!enabled) return null

  return (
    <div className={styles.container}>
      <OpenInLocalAgentButtonInner url={url} />
    </div>
  )
}

function OpenInLocalAgentButtonInner({url}: OpenInLocalAgentButtonProps) {
  const prompt = useMemo(() => buildPrompt(url), [url])
  const openInTools = useMemo<OpenInTool[]>(
    () => [
      {icon: VscodeIcon, label: 'VS Code', url: buildVSCodeAgentUrl(prompt)},
      {icon: CodexIcon, label: 'Codex', url: buildCodexUrl(prompt)},
      {icon: ClaudeCodeIcon, label: 'Claude Code', url: buildClaudeCodeUrl(prompt)},
    ],
    [prompt],
  )

  const handleCopyAsPrompt = useCallback(() => {
    void copyText(prompt)
  }, [prompt])

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Tooltip text="Open in coding tool" type="label">
          <ActionMenu.Button leadingVisual={TerminalIcon} size="medium" />
        </Tooltip>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay className={styles.overlay}>
        <ActionList>
          <ActionList.Item onSelect={handleCopyAsPrompt}>
            <ActionList.LeadingVisual>
              <TerminalIcon />
            </ActionList.LeadingVisual>
            Copy as prompt
            <ActionList.Description variant="block">
              <Truncate maxWidth={240} title={prompt}>
                {prompt}
              </Truncate>
            </ActionList.Description>
            <ActionList.TrailingVisual>
              <CopyIcon />
            </ActionList.TrailingVisual>
          </ActionList.Item>
          <ActionList.Divider className={styles.divider} />
          <ActionList.Group>
            <ActionList.GroupHeading>Open in</ActionList.GroupHeading>
            {openInTools.map(({icon: Icon, label, url: toolUrl}) => (
              <ActionList.Item key={label} onSelect={() => window.open(toolUrl, '_self')}>
                <ActionList.LeadingVisual>
                  <Icon />
                </ActionList.LeadingVisual>
                {label}
              </ActionList.Item>
            ))}
          </ActionList.Group>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
